"use client";

import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  DollarSign,
} from "lucide-react";
import * as React from "react";

import { Button } from "@/components/base/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/base/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/base/dropdown-menu";
import { cn } from "@/lib/utils";
import { Separator } from "../base/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "../base/tooltip";
import { formatCurrency } from "@/lib/currency";

// Generic option interface that can be nested recursively
export interface GenericOption {
  value: string;
  label: string;
  price?: number;
  unit?: string;
  coverage?: number | null;
  key?: string; // For labor types which use 'key' instead of 'value'
  name?: string; // For labor types which use 'name' instead of 'label'
  children?: GenericOption[];
}

interface LevelConfig {
  placeholder: string;
  backButtonLabel: string;
}

interface MultiLevelComboboxProps {
  options: GenericOption[];
  name?: string;
  width?: string;
  placeholder?: string;
  emptyMessage?: string;
  defaultValues?: string[]; // Array of default values for each level
  onSelectionChange?: (values: string[], labels: string[], fullSelection: GenericOption | null) => void;
  style?: React.CSSProperties;
  compact?: boolean;
  showPriceFilter?: boolean;
  levelConfigs?: LevelConfig[]; // Configuration for each level
  displayFormat?: (selection: GenericOption[]) => string; // Custom display format
}

export function MultiLevelCombobox({
  options,
  width = undefined,
  placeholder = "Select option...",
  emptyMessage = "No option found.",
  defaultValues = [],
  onSelectionChange,
  name = undefined,
  style = {},
  compact = false,
  showPriceFilter = false,
  levelConfigs = [],
  displayFormat,
}: Readonly<MultiLevelComboboxProps>) {
  // Normalize options to always use 'value' and 'label'
  const normalizeOptions = React.useCallback((opts: GenericOption[]): GenericOption[] => {
    return opts.map(opt => ({
      ...opt,
      value: opt.value || opt.key || '',
      label: opt.label || opt.name || '',
      children: opt.children ? normalizeOptions(opt.children) : undefined,
    }));
  }, []);

  const normalizedOptions = React.useMemo(() => normalizeOptions(options), [options, normalizeOptions]);

  // State for each level (up to 4 levels)
  const [open, setOpen] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValues);
  const [searchValues, setSearchValues] = React.useState<string[]>(['', '', '', '']);
  const [isPriceFilterEnabled, setIsPriceFilterEnabled] = React.useState(false);
  
  // Initialize currentLevel based on defaultValues
  const getInitialLevel = () => {
    // Find the last non-empty value to determine which level to show
    for (let i = defaultValues.length - 1; i >= 0; i--) {
      if (defaultValues[i]) {
        // Check if this is a leaf node or if it has children
        let currentOpts = normalizedOptions;
        for (let j = 0; j <= i; j++) {
          const opt = currentOpts.find(o => o.value === defaultValues[j]);
          if (!opt) break;
          if (j === i) {
            // We're at the target level, check if it has children
            return opt.children && opt.children.length > 0 ? i + 1 : 0;
          }
          currentOpts = opt.children || [];
        }
      }
    }
    return 0;
  };
  
  const [currentLevel, setCurrentLevel] = React.useState(getInitialLevel());

  // Update selectedValues when defaultValues prop changes
  React.useEffect(() => {
    setSelectedValues(defaultValues);
    
    // Recalculate current level based on new default values
    let newCurrentLevel = 0;
    for (let i = defaultValues.length - 1; i >= 0; i--) {
      if (defaultValues[i]) {
        // Check if this is a leaf node or if it has children
        let currentOpts = normalizedOptions;
        for (let j = 0; j <= i; j++) {
          const opt = currentOpts.find(o => o.value === defaultValues[j]);
          if (!opt) break;
          if (j === i) {
            // We're at the target level, check if it has children
            newCurrentLevel = opt.children && opt.children.length > 0 ? i + 1 : 0;
            break;
          }
          currentOpts = opt.children || [];
        }
        break;
      }
    }
    setCurrentLevel(newCurrentLevel);
  }, [defaultValues, normalizedOptions]);

  const displayValueSpanClasses = "overflow-hidden text-ellipsis";

  // Get the maximum depth of the options
  const getMaxDepth = React.useCallback((opts: GenericOption[], depth = 0): number => {
    if (!opts || opts.length === 0) return depth;
    
    const hasChildren = opts.some(opt => opt.children && opt.children.length > 0);
    if (!hasChildren) return depth;
    
    return Math.max(...opts.map(opt => 
      opt.children ? getMaxDepth(opt.children, depth + 1) : depth
    ));
  }, []);

  const maxDepth = React.useMemo(() => getMaxDepth(normalizedOptions), [normalizedOptions, getMaxDepth]);

  // Get options for current level based on selected values
  const getCurrentLevelOptions = React.useCallback((level: number): GenericOption[] => {
    let currentOptions = normalizedOptions;
    
    for (let i = 0; i < level; i++) {
      const selectedValue = selectedValues[i];
      if (!selectedValue) return [];
      
      const selectedOption = currentOptions.find(opt => opt.value === selectedValue);
      if (!selectedOption || !selectedOption.children) return [];
      
      currentOptions = selectedOption.children;
    }
    
    return currentOptions;
  }, [normalizedOptions, selectedValues]);

  // Filter options based on search and price filter
  const filterOptions = React.useCallback((opts: GenericOption[], level: number): GenericOption[] => {
    const searchValue = searchValues[level];
    
    return opts.filter(opt => {
      // Search filter
      if (searchValue && !opt.label.toLowerCase().includes(searchValue.toLowerCase())) {
        return false;
      }
      
      // Price filter - check if this option or any of its descendants has a price
      if (showPriceFilter && isPriceFilterEnabled) {
        const hasPrice = (option: GenericOption): boolean => {
          // If this option has a price, it passes
          if (option.price !== undefined && option.price !== null && option.price > 0) {
            return true;
          }
          
          // If it has children, check if any child has a price
          if (option.children && option.children.length > 0) {
            return option.children.some(child => hasPrice(child));
          }
          
          return false;
        };
        
        return hasPrice(opt);
      }
      
      return true;
    });
  }, [searchValues, isPriceFilterEnabled, showPriceFilter]);

  // Get selected option at a specific level
  const getSelectedOption = React.useCallback((level: number): GenericOption | null => {
    const levelOptions = getCurrentLevelOptions(level);
    const selectedValue = selectedValues[level];
    
    if (!selectedValue) return null;
    
    return levelOptions.find(opt => opt.value === selectedValue) || null;
  }, [getCurrentLevelOptions, selectedValues]);

  // Get all selected options
  const getAllSelectedOptions = React.useCallback((): GenericOption[] => {
    const selected: GenericOption[] = [];
    
    for (let i = 0; i <= maxDepth; i++) {
      const option = getSelectedOption(i);
      if (option) {
        selected.push(option);
      } else {
        break;
      }
    }
    
    return selected;
  }, [getSelectedOption, maxDepth]);

  // Handle selection at a specific level
  const handleSelection = React.useCallback((level: number, value: string) => {
    const newSelectedValues = [...selectedValues];
    
    // Set the value at the current level
    newSelectedValues[level] = value;
    
    // Clear all subsequent levels
    for (let i = level + 1; i <= 3; i++) { // Max 4 levels (0-3)
      newSelectedValues[i] = '';
    }
    
    setSelectedValues(newSelectedValues);
    
    // Check if this is a leaf node
    const levelOptions = getCurrentLevelOptions(level);
    const selectedOption = levelOptions.find(opt => opt.value === value);
    
    if (selectedOption && !selectedOption.children) {
      // This is a leaf node, close the dropdown
      setOpen(false);
      
      // Notify about the selection
      if (onSelectionChange) {
        // Need to get updated selection after state change
        setTimeout(() => {
          const allSelected = [];
          let currentOpts = normalizedOptions;
          
          for (let i = 0; i <= level; i++) {
            const val = i === level ? value : newSelectedValues[i];
            const opt = currentOpts.find(o => o.value === val);
            if (opt) {
              allSelected.push(opt);
              currentOpts = opt.children || [];
            }
          }
          
          const values = allSelected.map(opt => opt.value);
          const labels = allSelected.map(opt => opt.label);
          onSelectionChange(values, labels, selectedOption);
        }, 0);
      }
    } else if (selectedOption && selectedOption.children) {
      // Move to next level
      setCurrentLevel(level + 1);
    }
  }, [selectedValues, getCurrentLevelOptions, onSelectionChange, normalizedOptions]);

  // Default display format
  const defaultDisplayFormat = React.useCallback((selection: GenericOption[]): string => {
    if (selection.length === 0) return '';
    
    // For product styles: Style Brand Type format
    if (selection.length === 4) {
      const [type, brand, style] = selection;
      return `${style.label} ${brand.label !== "Any" ? brand.label : ""} ${type.label}`.trim();
    }
    
    // For other cases, just join the labels
    return selection.map(opt => opt.label).join(' ');
  }, []);

  const renderDisplayValue = () => {
    const allSelected = getAllSelectedOptions();
    if (allSelected.length === 0) return placeholder;
    
    const displayFn = displayFormat || defaultDisplayFormat;
    return (
      <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap text-ellipsis">
        <span className={cn(displayValueSpanClasses)}>
          {displayFn(allSelected)}
        </span>
      </div>
    );
  };

  const renderBackButton = (level: number) => {
    const config = levelConfigs[level - 1];
    const label = config?.backButtonLabel || `Back to level ${level}`;
    
    return (
      <React.Fragment>
        <Button
          variant="ghost"
          size="xs"
          className="m-1 font-normal"
          onClick={() => {
            setCurrentLevel(level - 1);
            const newSelectedValues = [...selectedValues];
            newSelectedValues[level] = '';
            setSelectedValues(newSelectedValues);
          }}
        >
          <span className="inline-flex items-center w-full gap-2">
            <ArrowLeft className="!w-3 !h-3" /> {label}
          </span>
        </Button>
        <Separator />
      </React.Fragment>
    );
  };

  const renderCommandInput = (level: number) => {
    const config = levelConfigs[level];
    const placeholderText = config?.placeholder || `Search level ${level + 1}...`;
    
    if (showPriceFilter) {
      return (
        <div className="grid grid-cols-[1fr_auto] w-full border-b">
          <div className="[&>*]:border-b-0">
            <CommandInput
              placeholder={placeholderText}
              value={searchValues[level]}
              onValueChange={(value) => {
                const newSearchValues = [...searchValues];
                newSearchValues[level] = value;
                setSearchValues(newSearchValues);
              }}
            />
          </div>
          <div className="flex items-center p-1 border-l">
            {renderPriceFilterButton()}
          </div>
        </div>
      );
    }
    
    return (
      <CommandInput
        placeholder={placeholderText}
        value={searchValues[level]}
        onValueChange={(value) => {
          const newSearchValues = [...searchValues];
          newSearchValues[level] = value;
          setSearchValues(newSearchValues);
        }}
      />
    );
  };

  const renderPriceFilterButton = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={!isPriceFilterEnabled ? "ghost" : "default"}
              className="relative w-full h-full"
              onClick={() => {
                const newFilterState = !isPriceFilterEnabled;
                setIsPriceFilterEnabled(newFilterState);
                
                // If enabling filter, check if current selections are still valid
                if (newFilterState) {
                  // Check each level to see if selections are still valid
                  let shouldReset = false;
                  let currentOpts = normalizedOptions;
                  
                  for (let i = 0; i <= maxDepth; i++) {
                    const filtered = filterOptions(currentOpts, i);
                    const selectedValue = selectedValues[i];
                    
                    if (selectedValue && !filtered.find(opt => opt.value === selectedValue)) {
                      shouldReset = true;
                      break;
                    }
                    
                    const selectedOpt = currentOpts.find(opt => opt.value === selectedValue);
                    if (selectedOpt?.children) {
                      currentOpts = selectedOpt.children;
                    } else {
                      break;
                    }
                  }
                  
                  if (shouldReset) {
                    // Reset to first level
                    setSelectedValues([]);
                    setCurrentLevel(0);
                  }
                }
              }}
            >
              <div className="relative">
                <DollarSign className="w-4 h-4" />
                {!isPriceFilterEnabled && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-[2px] -rotate-45">
                      <div className="w-full h-[1px] bg-foreground"></div>
                      <div className="w-full h-[1px] bg-card"></div>
                    </div>
                  </div>
                )}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>
              <p>
                {!isPriceFilterEnabled ? "Hide" : "Show"} items with no price
              </p>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderLevel = (level: number) => {
    const levelOptions = getCurrentLevelOptions(level);
    const filteredOptions = filterOptions(levelOptions, level);
    const selectedValue = selectedValues[level];
    const isLeafLevel = level === maxDepth || 
      (selectedValue && levelOptions.find(opt => opt.value === selectedValue)?.children === undefined);
    
    return (
      <Command
        shouldFilter={false}
        className={cn(
          "bg-white transition-all ease-in-out duration-300 relative",
          currentLevel === level ? "translate-x-0" : 
          currentLevel > level ? "-translate-x-full absolute" : "translate-x-full absolute"
        )}
      >
        {level > 0 && renderBackButton(level)}
        {renderCommandInput(level)}
        <CommandList>
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandGroup>
            {filteredOptions.map((option) => (
              <CommandItem
                className="flex"
                key={option.value}
                value={option.value}
                onSelect={(value) => handleSelection(level, value)}
              >
                <div className="flex items-center justify-between w-full gap-2">
                  <span>{option.label}</span>
                  {isLeafLevel && option.price !== undefined && (
                    <span>{formatCurrency(option.price)}</span>
                  )}
                </div>
                <Check
                  className={cn(
                    "ml-auto",
                    selectedValue === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    );
  };

  // Reset search values when dropdown closes
  React.useEffect(() => {
    if (!open) {
      setSearchValues(['', '', '', '']);
      setCurrentLevel(0);
    }
  }, [open]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                name={name}
                role="combobox"
                aria-expanded={open}
                className={cn(
                  "justify-between",
                  width ? `w-[${width}]` : "w-full",
                  getAllSelectedOptions().length === 0
                    ? "text-muted-foreground hover:text-muted-foreground"
                    : "",
                  compact && "h-7 text-xs py-1 px-2"
                )}
                style={style}
              >
                {renderDisplayValue()}
                <ChevronDown className="opacity-50" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>
              <div className="flex items-center gap-2">
                {getAllSelectedOptions().map((opt, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <ChevronRight className="opacity-50 shrink-0" />}
                    <span className={cn(displayValueSpanClasses, "shrink-0")}>
                      {opt.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent
        className={cn("p-0 relative overflow-x-hidden", width && `w-[${width}]`)}
      >
        {Array.from({ length: maxDepth + 1 }, (_, i) => renderLevel(i))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 